import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DeliveryStep = ({ onNext, deliveryData, setDeliveryData }) => {
  const [selectedAddress, setSelectedAddress] = useState(deliveryData.selectedAddress || '');
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(deliveryData.timeSlot || '');
  const [specialInstructions, setSpecialInstructions] = useState(deliveryData.instructions || '');
  const [errors, setErrors] = useState({});
  const [newAddress, setNewAddress] = useState({
    fullName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  const savedAddresses = [
    {
      id: 1,
      label: 'Home',
      fullName: 'John Smith',
      street: '123 Main Street',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      phone: '(555) 123-4567',
      isDefault: true
    },
    {
      id: 2,
      label: 'Office',
      fullName: 'John Smith',
      street: '456 Business Ave, Suite 200',
      city: 'New York',
      state: 'NY',
      zipCode: '10002',
      phone: '(555) 987-6543',
      isDefault: false
    }
  ];

  const timeSlots = [
    { id: 1, time: '9:00 AM - 11:00 AM', available: true, price: 0 },
    { id: 2, time: '11:00 AM - 1:00 PM', available: true, price: 0 },
    { id: 3, time: '1:00 PM - 3:00 PM', available: false, price: 0 },
    { id: 4, time: '3:00 PM - 5:00 PM', available: true, price: 2.99 },
    { id: 5, time: '5:00 PM - 7:00 PM', available: true, price: 4.99 },
    { id: 6, time: '7:00 PM - 9:00 PM', available: true, price: 4.99 }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!selectedAddress && !showNewAddressForm) {
      newErrors.address = 'Please select a delivery address';
    }

    if (showNewAddressForm) {
      if (!newAddress.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!newAddress.street.trim()) newErrors.street = 'Street address is required';
      if (!newAddress.city.trim()) newErrors.city = 'City is required';
      if (!newAddress.state.trim()) newErrors.state = 'State is required';
      if (!newAddress.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
      if (!newAddress.phone.trim()) newErrors.phone = 'Phone number is required';
    }

    if (!selectedTimeSlot) {
      newErrors.timeSlot = 'Please select a delivery time slot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      const deliveryInfo = {
        selectedAddress: showNewAddressForm ? newAddress : selectedAddress,
        timeSlot: selectedTimeSlot,
        instructions: specialInstructions,
        isNewAddress: showNewAddressForm
      };
      
      setDeliveryData(deliveryInfo);
      onNext();
    }
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddress(addressId);
    setShowNewAddressForm(false);
    setErrors({ ...errors, address: '' });
  };

  const handleNewAddressToggle = () => {
    setShowNewAddressForm(!showNewAddressForm);
    setSelectedAddress('');
    setErrors({ ...errors, address: '' });
  };

  const handleNewAddressChange = (field, value) => {
    setNewAddress({ ...newAddress, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleTimeSlotSelect = (slotId) => {
    setSelectedTimeSlot(slotId);
    setErrors({ ...errors, timeSlot: '' });
  };

  return (
    <div className="space-y-8">
      {/* Delivery Address Section */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Delivery Address
        </h3>
        
        {/* Saved Addresses */}
        <div className="space-y-3 mb-4">
          {savedAddresses.map((address) => (
            <div
              key={address.id}
              className={`
                p-4 border rounded-card cursor-pointer transition-smooth
                ${selectedAddress === address.id 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
              onClick={() => handleAddressSelect(address.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5
                    ${selectedAddress === address.id 
                      ? 'border-primary bg-primary' :'border-border'
                    }
                  `}>
                    {selectedAddress === address.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-body-semibold text-text-primary">
                        {address.label}
                      </span>
                      {address.isDefault && (
                        <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-input">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-text-primary mt-1">{address.fullName}</p>
                    <p className="text-text-secondary text-sm">
                      {address.street}, {address.city}, {address.state} {address.zipCode}
                    </p>
                    <p className="text-text-secondary text-sm">{address.phone}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  className="p-1"
                  iconName="Edit"
                  iconSize={16}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Add New Address Button */}
        <Button
          variant="outline"
          onClick={handleNewAddressToggle}
          iconName="Plus"
          iconPosition="left"
          className="mb-4"
        >
          Add New Address
        </Button>

        {/* New Address Form */}
        {showNewAddressForm && (
          <div className="p-4 border border-border rounded-card bg-background space-y-4">
            <h4 className="font-body-semibold text-text-primary">Add New Address</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  type="text"
                  placeholder="Full Name"
                  value={newAddress.fullName}
                  onChange={(e) => handleNewAddressChange('fullName', e.target.value)}
                  className={errors.fullName ? 'border-error' : ''}
                />
                {errors.fullName && (
                  <p className="text-error text-sm mt-1">{errors.fullName}</p>
                )}
              </div>
              
              <div>
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={newAddress.phone}
                  onChange={(e) => handleNewAddressChange('phone', e.target.value)}
                  className={errors.phone ? 'border-error' : ''}
                />
                {errors.phone && (
                  <p className="text-error text-sm mt-1">{errors.phone}</p>
                )}
              </div>
            </div>

            <div>
              <Input
                type="text"
                placeholder="Street Address"
                value={newAddress.street}
                onChange={(e) => handleNewAddressChange('street', e.target.value)}
                className={errors.street ? 'border-error' : ''}
              />
              {errors.street && (
                <p className="text-error text-sm mt-1">{errors.street}</p>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Input
                  type="text"
                  placeholder="City"
                  value={newAddress.city}
                  onChange={(e) => handleNewAddressChange('city', e.target.value)}
                  className={errors.city ? 'border-error' : ''}
                />
                {errors.city && (
                  <p className="text-error text-sm mt-1">{errors.city}</p>
                )}
              </div>
              
              <div>
                <Input
                  type="text"
                  placeholder="State"
                  value={newAddress.state}
                  onChange={(e) => handleNewAddressChange('state', e.target.value)}
                  className={errors.state ? 'border-error' : ''}
                />
                {errors.state && (
                  <p className="text-error text-sm mt-1">{errors.state}</p>
                )}
              </div>
              
              <div>
                <Input
                  type="text"
                  placeholder="ZIP Code"
                  value={newAddress.zipCode}
                  onChange={(e) => handleNewAddressChange('zipCode', e.target.value)}
                  className={errors.zipCode ? 'border-error' : ''}
                />
                {errors.zipCode && (
                  <p className="text-error text-sm mt-1">{errors.zipCode}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {errors.address && (
          <p className="text-error text-sm mt-2">{errors.address}</p>
        )}
      </div>

      {/* Delivery Time Slot Section */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Delivery Time Slot
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {timeSlots.map((slot) => (
            <div
              key={slot.id}
              className={`
                p-4 border rounded-card transition-smooth
                ${!slot.available 
                  ? 'border-border bg-gray-50 cursor-not-allowed opacity-50' 
                  : selectedTimeSlot === slot.id
                    ? 'border-primary bg-primary/5 cursor-pointer' :'border-border hover:border-primary/50 cursor-pointer'
                }
              `}
              onClick={() => slot.available && handleTimeSlotSelect(slot.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${selectedTimeSlot === slot.id 
                      ? 'border-primary bg-primary' :'border-border'
                    }
                  `}>
                    {selectedTimeSlot === slot.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <p className="font-body-semibold text-text-primary">
                      {slot.time}
                    </p>
                    {!slot.available && (
                      <p className="text-error text-sm">Not Available</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {slot.price > 0 ? (
                    <span className="font-data font-data-medium text-primary">
                      +${slot.price.toFixed(2)}
                    </span>
                  ) : (
                    <span className="font-data font-data-medium text-success">
                      Free
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {errors.timeSlot && (
          <p className="text-error text-sm mt-2">{errors.timeSlot}</p>
        )}
      </div>

      {/* Special Instructions */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Special Delivery Instructions
        </h3>
        
        <textarea
          placeholder="Add any special instructions for delivery (e.g., gate code, apartment number, leave at door)"
          value={specialInstructions}
          onChange={(e) => setSpecialInstructions(e.target.value)}
          className="w-full p-3 border border-border rounded-input resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
          rows={3}
        />
      </div>

      {/* Continue Button */}
      <div className="flex justify-end pt-4">
        <Button
          variant="primary"
          onClick={handleContinue}
          iconName="ArrowRight"
          iconPosition="right"
          className="px-8"
        >
          Continue to Payment
        </Button>
      </div>
    </div>
  );
};

export default DeliveryStep;